// Google Workspace 監査スケジュール設定タブ。週次/月次の頻度・曜日/日・実行時刻を入力し、
// 保存（PUT /gw/audit/schedule）/解除（DELETE）する。SF schedule.ts の buildLiveConfigCheckCard を
// ミラー（GW は単一カード・カレンダー無し）。frequency は weekly|monthly のみ（実フォームは
// enabled 前提。無効化は「スケジュールを解除」で行う）。
// 通知先はバックエンドがテナント管理者から解決するため送信しない（SP3b 決定）。
import { createBadge } from "../ui/badge";
import type { ScheduleDto, SchedulePutBody } from "./schedule-api";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

export interface ScheduleDeps {
  readonly schedule: ScheduleDto;
  readonly onSave: (body: SchedulePutBody) => Promise<void>;
  readonly onDisable: () => Promise<void>;
}

function labeledSelect(
  id: string,
  labelText: string,
  options: ReadonlyArray<{ value: string; label: string }>,
  selected: string,
  onChange: (value: string) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  const label = document.createElement("label");
  label.className = "block text-sm font-medium text-slate-700";
  label.htmlFor = id;
  label.textContent = labelText;
  const sel = document.createElement("select");
  sel.id = id;
  sel.className = "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm";
  for (const o of options) {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.textContent = o.label;
    sel.append(opt);
  }
  sel.value = selected; // 全 option 追加後に現在値を選択（happy-dom で確実に反映）。
  sel.addEventListener("change", () => onChange(sel.value));
  wrap.append(label, sel);
  return wrap;
}

// GW 設定チェックの定期実行スケジュールを設定するカード。現在値で初期化し、
// 保存=onSave / 解除=onDisable を呼ぶ。
export function renderGwSchedule(root: HTMLElement, deps: ScheduleDeps): void {
  root.replaceChildren();
  const dto = deps.schedule;
  // 現在値で初期化（未設定は既定 weekly/月曜/09:00）。
  const state: { frequency: "weekly" | "monthly"; dayOfWeek: number; dayOfMonth: number; time: string } = {
    frequency: dto.frequency ?? "weekly",
    dayOfWeek: dto.dayOfWeek ?? 1,
    dayOfMonth: dto.dayOfMonth ?? 1,
    time: dto.time ?? "09:00",
  };

  const card = document.createElement("section");
  card.dataset["testid"] = "job-card";
  card.className = "max-w-xl rounded-lg border border-slate-200 p-4";

  const heading = document.createElement("h2");
  heading.className = "flex items-center gap-2 text-base font-medium text-slate-900";
  heading.append(createBadge({ label: "設定チェック", tone: "info" }));
  card.append(heading);

  const intro = document.createElement("p");
  intro.className = "mt-2 text-sm text-slate-600";
  intro.textContent = "設定チェック診断を定期的に自動実行します。実行結果は実行履歴に記録され、検出があればテナント管理者へ通知します。";
  card.append(intro);

  // 現在の状態表示（enabled / status）。
  const statusLine = document.createElement("p");
  statusLine.dataset["testid"] = "schedule-current-status";
  statusLine.className = "mt-2 text-sm text-slate-600";
  const stateLabel = dto.enabled ? "有効" : "無効";
  const statusText = dto.status !== undefined && dto.status !== "" ? "（" + dto.status + "）" : "";
  statusLine.textContent = "現在の状態: " + stateLabel + statusText;
  card.append(statusLine);

  const form = document.createElement("form");
  form.className = "mt-3 space-y-3";

  const conditional = document.createElement("div");
  function renderConditional(): void {
    conditional.replaceChildren();
    if (state.frequency === "weekly") {
      conditional.append(
        labeledSelect(
          "sch-gw-config-check-weekday",
          "曜日",
          WEEKDAYS.map((w, i) => ({ value: String(i), label: w + "曜日" })),
          String(state.dayOfWeek),
          (v) => {
            state.dayOfWeek = Number(v);
          },
        ),
      );
    } else {
      const days: Array<{ value: string; label: string }> = [];
      for (let d = 1; d <= 28; d += 1) days.push({ value: String(d), label: d + "日" });
      conditional.append(
        labeledSelect("sch-gw-config-check-monthday", "日", days, String(state.dayOfMonth), (v) => {
          state.dayOfMonth = Number(v);
        }),
      );
    }
  }

  const freq = labeledSelect(
    "sch-gw-config-check-frequency",
    "頻度",
    [
      { value: "weekly", label: "週次" },
      { value: "monthly", label: "月次" },
    ],
    state.frequency,
    (v) => {
      state.frequency = v === "monthly" ? "monthly" : "weekly";
      renderConditional();
    },
  );

  const timeWrap = document.createElement("div");
  const timeLabel = document.createElement("label");
  timeLabel.className = "block text-sm font-medium text-slate-700";
  timeLabel.htmlFor = "sch-gw-config-check-time";
  timeLabel.textContent = "実行時刻";
  const timeInput = document.createElement("input");
  timeInput.id = "sch-gw-config-check-time";
  timeInput.type = "time";
  timeInput.value = state.time;
  timeInput.className = "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm";
  timeInput.addEventListener("change", () => {
    state.time = timeInput.value;
  });
  timeWrap.append(timeLabel, timeInput);

  const buttons = document.createElement("div");
  buttons.className = "flex flex-wrap items-center gap-2";

  const save = document.createElement("button");
  save.type = "button";
  save.dataset["testid"] = "schedule-save";
  save.className = "rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  save.textContent = "保存";
  save.addEventListener("click", () => {
    void deps.onSave({
      frequency: state.frequency,
      dayOfWeek: state.dayOfWeek,
      dayOfMonth: state.dayOfMonth,
      time: state.time,
      // 通知先はバックエンドがテナント管理者から解決するため送信しない（SP3b 決定）。
    });
  });
  buttons.append(save);

  if (dto.enabled) {
    const disable = document.createElement("button");
    disable.type = "button";
    disable.dataset["testid"] = "schedule-disable";
    disable.className =
      "rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50";
    disable.textContent = "スケジュールを解除";
    disable.addEventListener("click", () => {
      void deps.onDisable();
    });
    buttons.append(disable);
  }

  form.append(freq, conditional, timeWrap, buttons);
  form.addEventListener("submit", (e) => e.preventDefault());
  card.append(form);
  renderConditional();
  root.append(card);
}
